import { KeywordLink } from "@/components/common/MarkdownText";

export interface ContextLinkRule {
  /** The exact word or phrase to be highlighted and linked */
  target: string;
  /** Destination URL (relative path like "/tools/course-finder" or external "https://...") */
  link: string;
  /** Match exact word boundaries (default: true) */
  wordBoundary?: boolean;
  /**
   * Conditions for where this link should be active.
   * If match is not specified, it will apply generally where called.
   */
  match?: {
    country?: string | string[];
    university?: string | string[];
    course?: string | string[];
    page?: string | string[];
  };
}

/**
 * =========================================================================
 * 📌 CENTRALIZED CONTEXTUAL LINKS REGISTRY
 * =========================================================================
 * To add a new link anywhere on the website, simply add a new rule below!
 */
export const CONTEXTUAL_LINK_RULES: ContextLinkRule[] = [
  // 1. Australia Country Page
  {
    target: "globally ranked universities",
    link: "https://www.topuniversities.com/student-info/choosing-university/worlds-top-100-universities",
    match: { country: ["australia"] },
  },

  // 2. Trinity College Dublin (University Page)
  {
    target: "medicine",
    link: "/ireland/trinity-college-dublin/bsc-hons-5-years-pharmacy/course-details",
    wordBoundary: true,
    match: { university: ["trinity-college-dublin", "trinity college"] },
  },

  // 3. University of Wales Trinity Saint David (Course Curriculum)
  {
    target: "Data Science and Analytics",
    link: "/blogs/msc-data-and-artificial-intelligence-at-university-of-wales-trinity-saint-david",
    match: {
      course: ["msc-1-year-data-science-and-analytics", "data-science"],
      university: ["trinity-saint-david", "wales", "university of wales"],
    },
  },
];

export interface MatchContext {
  country?: string | null;
  university?: string | null;
  course?: string | null;
  page?: string | null;
}

const isMatch = (
  value: string | null | undefined,
  criterion?: string | string[]
): boolean => {
  if (!criterion) return false;
  if (!value) return false;

  const val = value.toLowerCase().trim();
  const criteria = Array.isArray(criterion) ? criterion : [criterion];

  return criteria.some((c) => {
    const item = c.toLowerCase().trim();
    return val === item || val.includes(item);
  });
};

/**
 * Returns matching keyword links for a given context (country, university, course, or page).
 */
export function getContextualLinks(context: MatchContext): KeywordLink[] {
  const matched = CONTEXTUAL_LINK_RULES.filter((rule) => {
    if (!rule.match) return true;

    const { country, university, course, page } = rule.match;

    if (country && isMatch(context.country, country)) return true;
    if (university && isMatch(context.university, university)) return true;
    if (course && isMatch(context.course, course)) return true;
    if (page && isMatch(context.page, page)) return true;

    return false;
  });

  return matched.map(({ target, link, wordBoundary }) => ({
    target,
    link,
    wordBoundary: wordBoundary ?? true,
  }));
}

/** Quick helper for Country components */
export function getCountryContextLinks(countryNameOrSlug?: string | null): KeywordLink[] {
  if (!countryNameOrSlug) return [];
  return getContextualLinks({ country: countryNameOrSlug });
}

/** Quick helper for University components */
export function getUniversityContextLinks(
  universitySlugOrName?: string | null
): KeywordLink[] {
  if (!universitySlugOrName) return [];
  return getContextualLinks({ university: universitySlugOrName });
}

/** Quick helper for Course components */
export function getCourseContextLinks(
  courseSlugOrName?: string | null,
  universitySlugOrName?: string | null
): KeywordLink[] {
  return getContextualLinks({
    course: courseSlugOrName,
    university: universitySlugOrName,
  });
}
